import Row from '@app/components/layout/Row'
import { Dispatch, useCallback } from 'react'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { FileSelector } from '@app/components/core/input/text/FileSelector'
import { PreviewImage } from '@app/components/core/image/PreviewImage'
import { Style } from 'inlines'

export type ExistingImage = { id: number; src: string }
export type ImagePreview = { preview: string; file: File }

export type ImageEditPanelProps = {
  existingImages: ExistingImage[]
  newImages: ImagePreview[]
  removedImages: number[]
  placeholdersDisplayed?: number
  maxImages?: number
  iconStyle?: Style
  setNewImages: Dispatch<React.SetStateAction<ImagePreview[]>>
  setRemovedImages: Dispatch<React.SetStateAction<number[]>>
}

export const ImageEditRow = ({
  existingImages,
  newImages,
  removedImages,
  placeholdersDisplayed,
  maxImages = 10,
  iconStyle,
  setNewImages,
  setRemovedImages,
}: ImageEditPanelProps) => {
  const deleteExisting = useCallback(
    (id: number) => {
      setRemovedImages((prev) => [...prev, id])
    },
    [setRemovedImages],
  )

  const cancelDeleteExisting = useCallback(
    (id: number) => {
      setRemovedImages((prev) => prev.filter((x) => x !== id))
    },
    [setRemovedImages],
  )

  const deleteNew = useCallback(
    (image: string) => {
      setNewImages((prev) => prev.filter((x) => x.preview !== image))
    },
    [setNewImages],
  )

  const imageCount =
    existingImages.length + newImages.length - removedImages.length
  const imagesToAdd = maxImages - imageCount

  const _placeHoldersToRender = placeholdersDisplayed
    ? Math.max(placeholdersDisplayed - imageCount, 0)
    : 0

  return (
    <Row style={{ flexWrap: 'wrap' }}>
      {existingImages.map((image, i) => (
        <PreviewImage
          key={`existing_${i}`}
          src={image.src}
          onDelete={() => deleteExisting(image.id)}
          onCancelDelete={
            removedImages.includes(image.id)
              ? () => cancelDeleteExisting(image.id)
              : null
          }
        />
      ))}
      {newImages.map((image, i) => (
        <PreviewImage
          key={`new_${i}`}
          src={image.preview}
          onDelete={() => deleteNew(image.preview)}
        />
      ))}

      <FileSelector
        icon={faPlus}
        maxFiles={imagesToAdd}
        iconStyle={{
          fontSize: '3rem',
          ...iconStyle,
        }}
        containerStyle={selectorStyle}
        onFileSelectionUpdated={(files) =>
          setNewImages((prev) => [
            ...prev,
            ...files.map((file) => ({
              preview: URL.createObjectURL(file),
              file,
            })),
          ])
        }
      />
    </Row>
  )
}

const selectorStyle: Style = {
  width: '5.625rem',
  height: '5.625rem',
  borderRadius: '1.25rem',
  aspectRatio: '1/1',
  flexShrink: 0,
  background: 'var(--gray-100)',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}
